import type { MembershipFreeze } from '@/types';
import { getBaliDateKey } from '@/lib';

/**
 * Returns the freeze period that contains today's date (Bali time), or null if none.
 * Freezes are stored as YYYY-MM-DD date strings, so we compare with today's Bali
 * (Asia/Makassar) YYYY-MM-DD key — independent of the admin's browser timezone.
 * Boundaries are inclusive: a freeze starting or ending today is considered current.
 */
export function getCurrentFreeze(
  freezes: MembershipFreeze[] | undefined | null
): MembershipFreeze | null {
  if (!Array.isArray(freezes) || freezes.length === 0) return null;
  const todayStr = getBaliDateKey(new Date());
  return freezes.find(f => todayStr >= f.start_date && todayStr <= f.end_date) || null;
}

/**
 * Validate freeze dates against membership period and existing freezes.
 * Returns an error message string, or null if valid.
 *
 * Freeze dates are INCLUSIVE on both ends: a freeze ending on the 16th is
 * still frozen on the 16th, and the member regains access on the 17th.
 * So two freezes conflict when their [start, end] ranges share any day —
 * a new freeze may start no earlier than the day after an existing one ends.
 */
export function validateFreezeDates(params: {
  startDate: string;
  endDate: string;
  membershipStartDate?: string;
  membershipEndDate?: string;
  existingFreezes: Array<{ id: string; start_date: string; end_date: string }>;
  editingFreezeId?: string;
}): string | null {
  const { startDate, endDate, membershipStartDate, membershipEndDate, existingFreezes, editingFreezeId } = params;

  // Freeze start must be within membership period
  if (membershipStartDate && startDate < membershipStartDate) {
    return 'Freeze start date cannot be before membership start date';
  }
  if (membershipEndDate && startDate > membershipEndDate) {
    return 'Freeze start date cannot be after membership end date';
  }

  // Freeze end must not exceed membership end
  if (membershipEndDate && endDate > membershipEndDate) {
    return 'Freeze end date cannot be after membership end date';
  }

  // No overlap with existing freezes (exclude the freeze being edited)
  for (const existing of existingFreezes) {
    if (editingFreezeId && existing.id === editingFreezeId) continue;
    if (startDate <= existing.end_date && endDate >= existing.start_date) {
      return `Overlaps with existing freeze (${existing.start_date} - ${existing.end_date})`;
    }
  }

  return null;
}
